import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/react'
import { connect } from 'react-redux'

import { CAMPAIGNS } from '@/constants'
import { filterAppsForLicense } from '@/utils/products'
import { getProtocolVersion } from '@/store/selectors'
import useCampaigns from '@/hooks/useCampaigns'
import mediaQueries from '@/layout/media-queries'
import ProductCard from '@/components/Product/Card'

const rowCss = (appsLength) => css`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  margin-left: -16px;
  margin-right: -16px;

  margin-bottom: 20px;

  ${mediaQueries.smallScreen()} {
    margin-bottom: 30px;
  }

  ${mediaQueries.xlargeScreen()} {
    margin-left: -20px;
    margin-right: -20px;
  }

  .item-card-base {
    ${mediaQueries.xlargeScreen()} {
      width: calc(25% - 40px);
      margin: 0 20px 40px 20px;
    }

    ${appsLength % 2 !== 0
      ? `
      ${mediaQueries.smallScreen()} {
        &:last-child {
          display: none;
        }
      }

      ${mediaQueries.largeScreen()} {
        &:last-child {
          display: flex;
        }
      }

      ${mediaQueries.xlargeScreen()} {      
        &:last-child {
          display: none;
        }
      }`
      : ``}
  }
`

const sectionTitleCss = css`
  margin-top: 0;
  margin-bottom: 32px;

  ${mediaQueries.xlargeScreen()} {
    margin-bottom: 40px;
  }
`

const getCampaignApps = (campaigns) => {
  return campaigns
    .filter(
      (campaign) =>
        !campaign.attributes.tags.includes([
          CAMPAIGNS.TAGS.APP_OF_THE_WEEK,
          CAMPAIGNS.TAGS.APP_OF_THE_MONTH,
          CAMPAIGNS.TAGS.APP_OF_THE_MONTH_RIGHT,
        ])
    )
    .reduce((appIds, campaign) => {
      campaign?.applications?.forEach((app) => {
        if (app.id && appIds.indexOf(app.id) === -1) {
          appIds.push(app.id)
        }
      })

      return appIds
    }, [])
}

const AppsList = ({ publishedApps, protocolVersion, className }) => {
  const { allCampaigns, apps } = useCampaigns()

  const hasPublishedApps = !!Object.keys(publishedApps).length
  publishedApps = hasPublishedApps ? publishedApps : apps
  const listKey = hasPublishedApps ? 'APPS_LIST_REACT' : 'APPS_LIST_FROM_GATSBY'

  const pickedApps = useMemo(
    () => {
      const pickedAppIds = getCampaignApps(allCampaigns)

      let pickedApps = pickedAppIds
        .filter((appId) => publishedApps[appId])
        .map((appId) => publishedApps[appId])

      // TODO: Filter out apps from visible campaigns
      pickedApps = filterAppsForLicense(pickedApps, [], protocolVersion).slice(
        0,
        9
      )

      return pickedApps || []
    },
    [allCampaigns, publishedApps, protocolVersion]
  )

  return (
    <section className={className}>
      <h5 css={sectionTitleCss}>Here are some apps you may like</h5>
      <div css={rowCss(pickedApps.length)} key={listKey}>
        {pickedApps.map((app) => (
          <ProductCard app={app} key={app.id} />
        ))}
      </div>
    </section>
  )
}

const mapStateToProps = (state) => {
  const protocolVersion = getProtocolVersion(state)
  // Use more up to date fetched data if exists
  const publishedApps = state.publishedApps.items

  return {
    publishedApps,
    protocolVersion,
  }
}

AppsList.propTypes = {
  publishedApps: PropTypes.object,
  protocolVersion: PropTypes.string,
  className: PropTypes.string,
}

export default connect(
  mapStateToProps,
  null
)(AppsList)
